import axios, { AxiosError, AxiosInstance } from 'axios';
import { logout } from '../utils/auth';

const API_CONFIG = {
  coreBaseURL: import.meta.env.VITE_CORE_API_URL || 'http://localhost:3000',
  intelBaseURL: import.meta.env.VITE_INTEL_API_URL || 'http://localhost:3001',
};

export default class ApiService {
  private readonly token: string;
  private coreApi: AxiosInstance;
  private intelApi: AxiosInstance;

  constructor(token: string) {
    this.token = token;

    // Initialize Core API client
    this.coreApi = axios.create({
      baseURL: API_CONFIG.coreBaseURL,
      headers: {
        'X-API-KEY': token,
        'Content-Type': 'application/json',
      },
    });

    // Initialize Intel API client
    this.intelApi = axios.create({
      baseURL: API_CONFIG.intelBaseURL,
      headers: {
        // TODO: add apikey imp later
        'X-API-KEY': '123456',
        'Content-Type': 'application/json',
      },
    });

    // Setup interceptors for both API instances
    this.setupInterceptors(this.coreApi);
    this.setupInterceptors(this.intelApi);
  }

  private setupInterceptors(apiInstance: any) {
    apiInstance.interceptors.response.use(
      (response: any) => response.data,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access by logging out
          logout();
        }
        return Promise.reject(error);
      },
    );
  }

  // Core API Methods
  async getCoreEndpoint(endpoint: string, params?: any) {
    return this.coreApi.get(endpoint, { params });
  }

  async postCoreEndpoint(endpoint: string, data: any) {
    return this.coreApi.post(endpoint, data);
  }

  async putCoreEndpoint(endpoint: string, data: any) {
    return this.coreApi.put(endpoint, data);
  }

  async deleteCoreEndpoint(endpoint: string) {
    return this.coreApi.delete(endpoint);
  }

  // Intel API Methods
  async getIntelEndpoint(endpoint: string, params?: any) {
    return this.intelApi.get(endpoint, { params });
  }

  async postIntelEndpoint(endpoint: string, data: any) {
    return this.intelApi.post(endpoint, data);
  }

  async patchIntelEndpoint(endpoint: string, data: any) {
    return this.intelApi.patch(endpoint, data);
  }

  async deleteIntelEndpoint(endpoint: string) {
    return this.intelApi.delete(endpoint);
  }

  // Fund management methods
  async getFunds() {
    return this.getCoreEndpoint('/fund/list');
  }

  async getFund(fundId: string) {
    return this.getCoreEndpoint(`/fund/${fundId}`);
  }

  async getFundBalance(fundId: string, currency: string = 'USD') {
    return this.getCoreEndpoint(`/fund/${fundId}/balance`, { currency });
  }

  async getFundPositions(fundId: string) {
    return this.getCoreEndpoint(`/fund/${fundId}/positions`);
  }

  async getFundOrders(fundId: string) {
    return this.getCoreEndpoint(`/fund/${fundId}/orders`);
  }

  async getFundActivities(
    fundId: string,
    page: number = 1,
    limit: number = 20,
  ) {
    try {
      const response = await this.getCoreEndpoint(
        `/fund/${fundId}/activities`,
        { page, limit },
      );
      return response;
    } catch (error) {
      console.error('Error fetching fund activities:', error);
      throw error;
    }
  }

  async getUserList() {
    try {
      const response = await this.getCoreEndpoint('/fund/users');
      return response;
    } catch (error) {
      console.error('Error fetching user list:', error);
      throw error;
    }
  }

  async cancelOrder(fundId: string, orderId: number) {
    return this.deleteCoreEndpoint(`/fund/${fundId}/orders/${orderId}`);
  }

  async createFundOrder(
    fundId: string,
    orderData: {
      symbol: string;
      side: 'buy' | 'sell';
      type?: 'market' | 'limit' | 'stop' | 'stop_limit';
      quantity: number;
      limitPrice?: number;
      stopPrice?: number;
      duration?: 'day' | 'gtc' | 'gfd';
      tag?: string;
      isPreview?: boolean;
    },
  ) {
    return this.postCoreEndpoint(`/fund/${fundId}/buy`, orderData);
  }

  async createFund(fundData: {
    name: string;
    description?: string;
    image?: string;
    metadata?: Record<string, any>;
  }) {
    return this.postCoreEndpoint('/fund', fundData);
  }

  async addFundSource(
    fundId: string,
    sourceData: {
      sourceType: string;
      sourceId: string;
      data?: Record<string, any>;
    },
  ) {
    return this.postCoreEndpoint(`/fund/${fundId}/source`, sourceData);
  }

  async removeFundSource(fundId: string, sourceId: string) {
    return this.deleteCoreEndpoint(`/fund/${fundId}/source/${sourceId}`);
  }

  async addFundManager(fundId: string, managerId: string) {
    return this.postCoreEndpoint(`/fund/${fundId}/manager`, { managerId });
  }

  async removeFundManager(fundId: string, managerId: string) {
    return this.deleteCoreEndpoint(`/fund/${fundId}/manager/${managerId}`);
  }

  // Intel API Methods
  async searchIntel(query: string, page: number = 1, limit: number = 10) {
    return this.getIntelEndpoint('/search', { query, page, limit });
  }

  async searchWeb(query: string, source?: string) {
    return this.getIntelEndpoint('/search-web', { query, source });
  }

  async getQuotes(
    symbols: string[],
    type?: 'crypto' | 'stock',
    source?: string,
  ) {
    return this.postIntelEndpoint('/quotes', { symbols, type, source });
  }

  async getSnapshots(
    symbols: string[],
    type?: 'crypto' | 'stock',
    source?: string,
  ) {
    return this.postIntelEndpoint('/snapshots', { symbols, type, source });
  }

  async getHistoricalBars(
    symbols: string[],
    preset?: '1D' | '5D' | '30D' | '6M' | '1Y',
    type?: 'crypto' | 'stock',
    source?: string,
  ) {
    return this.postIntelEndpoint('/historical-bars', {
      symbols,
      preset,
      type,
      source,
    });
  }

  async getHistoricalBarsChart(
    symbols: string[],
    preset?: '1D' | '5D' | '30D' | '6M' | '1Y',
    type?: 'crypto' | 'stock',
    source?: string,
  ) {
    return this.postIntelEndpoint('/historical-bars/chart', {
      symbols,
      preset,
      type,
      source,
    });
  }

  async getEarningsCalendar(from: string, to: string) {
    return this.getIntelEndpoint('/earnings-calendar', { from, to });
  }

  async getAnalystRecommendations(symbol: string) {
    return this.getIntelEndpoint('/analyst-recommendations', { symbol });
  }

  async getSenateLobbying(symbol: string, from: string, to: string) {
    return this.getIntelEndpoint('/senate-lobbying', { symbol, from, to });
  }

  async getGovernmentSpending(
    symbol: string,
    from: string,
    to: string,
    country: string = 'USA',
  ) {
    return this.getIntelEndpoint('/government-spending', {
      symbol,
      from,
      to,
      country,
    });
  }

  async getFDACalendar() {
    return this.getIntelEndpoint('/fda-calendar');
  }

  async getInsiderSentiment(symbol: string, from: string, to: string) {
    return this.getIntelEndpoint('/insider-sentiment', { symbol, from, to });
  }

  async getInsiderTransactions(symbol: string, from: string, to: string) {
    return this.getIntelEndpoint('/insider-transactions', { symbol, from, to });
  }

  async getBasicFinancial(symbol: string) {
    return this.getIntelEndpoint('/basic-financial', { symbol });
  }

  async getOptionExpirations(symbol: string) {
    return this.getIntelEndpoint('/option-expirations', { symbol });
  }

  async getOptionChains(symbol: string, expiration: string) {
    return this.getIntelEndpoint('/option-chains', { symbol, expiration });
  }

  // Tag Methods
  async getTags(page: number = 1, limit: number = 10, name?: string) {
    const params: Record<string, any> = { page, limit };
    if (name) {
      params.name = name;
    }
    return this.getIntelEndpoint('/tags', params);
  }

  async createTag(data: { name: string; value: any }) {
    return this.postIntelEndpoint('/tags', data);
  }

  async deleteTag(id: number) {
    return this.deleteIntelEndpoint(`/tags/${id}`);
  }

  async updateTag(id: number, data: { name: string; value: any }) {
    return this.patchIntelEndpoint(`/tags/${id}`, data);
  }

  async getTagDefinitions(page: number = 1, limit: number = 10, name?: string, requiredOnly: boolean = false) {
    const params: Record<string, any> = { page, limit };
    if (name) {
      params.name = name;
    }
    if (requiredOnly) {
      params.requiredOnIngestion = true;
    }
    return this.getIntelEndpoint('/tag-definitions', params);
  }

  async createTagDefinition(data: { 
    name: string; 
    spec: any; 
    relatedTags?: string[];
    requiredOnIngestion?: boolean;
  }) {
    return this.postIntelEndpoint('/tag-definitions', data);
  }

  async deleteTagDefinition(id: number) {
    return this.deleteIntelEndpoint(`/tag-definitions/${id}`);
  }

  async updateTagDefinition(id: number, data: { 
    name: string; 
    requiredOnIngestion?: boolean;
    relatedTags?: string[] 
  }) {
    return this.patchIntelEndpoint(`/tag-definitions/${id}`, data);
  }

  async createTagDefinitionSpecVersion(tagDefinitionId: number, spec: any) {
    return this.postIntelEndpoint(`/tag-definitions/${tagDefinitionId}/versions`, { spec });
  }

  async searchTags(query: string, limit: number = 10) {
    return this.getIntelEndpoint('/tags/search', { query, limit });
  }

  async searchTagDefinitions(query: string, limit: number = 10) {
    return this.getIntelEndpoint('/tag-definitions/search', { query, limit });
  }
}
